import React, { useEffect, useState } from 'react';
import { type MetaFunction, useSearchParams } from 'react-router';

import ArcadeConfigForm from '~/components/ArcadeConfigForm/ArcadeConfigForm';
import Button from '~/components/Button';
import Login from '~/components/Login';
import Modal from '~/components/Modal';
import { useAuth } from '~/contexts/AuthContext';
import { type ArcadeConfigFormValues } from '~/hooks/useArcadeConfigForm';
import { getArcadeConfig } from '~/utils/storage';

export const meta: MetaFunction = ({ matches }) => {
  const parentMeta = matches
    .flatMap(match => match.meta ?? [])
    .filter(
      metaItem =>
        !('title' in metaItem) &&
        !('name' in metaItem && metaItem.name === 'description')
    );

  return [
    {
      title: 'Singleplayer setup | Respawwwn - Game Guesser | Gaming Challenge'
    },
    {
      property: 'og:title',
      content:
        'Singleplayer setup | Respawwwn - Game Guesser | Gaming Challenge'
    },
    {
      name: 'description',
      content:
        'Configure your singleplayer arcade game. Choose collections, difficulty, number of questions and time limits for your custom gaming challenge.'
    },
    {
      property: 'og:description',
      content:
        'Configure your singleplayer arcade game. Choose collections, difficulty, number of questions and time limits for your custom gaming challenge.'
    },
    ...parentMeta
  ];
};

export default function ArcadeConfigSingleplayer() {
  const { isAuthenticated } = useAuth();
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const [initialConfig, setInitialConfig] = useState<
    Partial<ArcadeConfigFormValues> | undefined
  >(undefined);

  const shouldReplay = searchParams.get('replay') === 'true';

  useEffect(() => {
    if (shouldReplay) {
      const savedConfig = getArcadeConfig();
      setInitialConfig(savedConfig ?? undefined);
    }
  }, [shouldReplay]);

  return (
    <div className="min-h-viewport mx-auto flex max-w-5xl flex-col gap-10 px-4 pb-safe-offset-4 pt-safe-offset-24 md:px-10 md:pb-10 md:pt-safe-offset-32">
      <div className="flex justify-stretch gap-3">
        <h1 className="h-3xl md:h-4xl">Create a Singleplayer Game</h1>
      </div>

      <div className="flex flex-col gap-4 md:gap-6">
        {isAuthenticated ? (
          <ArcadeConfigForm
            showNickname={false}
            initialConfig={initialConfig}
          />
        ) : (
          <div className="flex flex-col items-center gap-4">
            <p className="text-lg text-center">
              You need to be signed in to create a game.
            </p>
            <Button variant="green" onClick={() => setIsLoginModalOpen(true)}>
              Sign in to play
            </Button>
          </div>
        )}
      </div>

      {/* Login Modal */}
      <Modal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        size="small"
        disabled={isAuthenticated}
      >
        <Login />
      </Modal>
    </div>
  );
}
